import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CART } from "@/constants/testIds";
import { Minus, Plus, Trash2, ShoppingBag, Check, AlertCircle, Gift } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PayPalPanel } from "@/components/PayPalPanel";
import { track } from "@/lib/tracking";

const COUNTRIES = [
  ["PT", "Portugal"], ["FR", "France"], ["DE", "Germany"], ["IT", "Italy"],
  ["NL", "Netherlands"], ["BE", "Belgium"], ["GB", "United Kingdom"], ["CH", "Switzerland"],
  ["US", "United States"], ["OTHER", "Other"],
];

export function CartDrawer() {
  const { items, open, setOpen, remove, setQty, subtotal, country, setCountry, clear, referralCode, salesSuspended } = useCart();
  const { currency, format, formatAmount, convert } = useCurrency();
  const { token, customer } = useAuth();
  const { t, i18n } = useTranslation();
  const [postal, setPostal] = useState(() => localStorage.getItem("preshh_postal") || "");
  const [rates, setRates] = useState({ options: [], postal_valid: true, postal_meta: {} });
  const [selectedRateId, setSelectedRateId] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // {valid, discount_amount, code}
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [idDocument, setIdDocument] = useState("");
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [nif, setNif] = useState("");
  const [billingName, setBillingName] = useState("");

  const cartPayload = useMemo(() => items.map((i) => ({ product_id: i.id, quantity: i.quantity })), [items]);

  useEffect(() => {
    if (!open) return;
    setRatesLoading(true);
    api.post("/shipping/rates", { country, postal_code: postal, items: cartPayload })
      .then((r) => setRates(r.data))
      .catch(() => setRates({ options: [], postal_valid: false, postal_meta: {} }))
      .finally(() => setRatesLoading(false));
  }, [country, postal, open, cartPayload]);

  useEffect(() => { localStorage.setItem("preshh_postal", postal || ""); }, [postal]);

  // Keep a valid option selected: prefer Standard (Europe), fall back to the first available (e.g. Express-only destinations).
  useEffect(() => {
    const ids = (rates.options || []).map((o) => o.id);
    if (!ids.length) return;
    if (!ids.includes(selectedRateId)) setSelectedRateId(ids.includes("standard") ? "standard" : ids[0]);
  }, [rates.options]); // eslint-disable-line

  const selectedRate = rates.options.find((o) => o.id === selectedRateId) || rates.options[0];
  const needsIdDoc = !!rates?.notes?.import_duties; // outside-EU customs documentation
  const shipPrice = selectedRate?.price || 0;
  const discount = coupon?.valid ? coupon.discount_amount : 0;
  // Convert per-line (unit→currency, rounded) then sum, mirroring the backend charge exactly.
  const round2 = (n) => Math.round(n * 100) / 100;
  const subtotalConv = round2(items.reduce((s, i) => s + convert(i.price) * i.quantity, 0));
  const shipConv = convert(shipPrice);
  const discountConv = convert(discount);
  const vatRate = (() => {
    if (country !== "PT") return 0;
    const d = (postal || "").replace(/\D/g, "").slice(0, 4);
    if (d.length === 4) { const n = +d; if (n >= 9000 && n <= 9499) return 0.22; if (n >= 9500 && n <= 9999) return 0.16; }
    return 0.23;
  })();
  const netConv = round2(subtotalConv + shipConv - discountConv);
  const vatConv = round2(netConv * vatRate);
  const totalConv = round2(netConv + vatConv);

  // Auto-suggest birthday coupon if signed in + eligible
  useEffect(() => {
    if (!token || !open) return;
    api.get("/customer/birthday-benefit", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (r.data.eligible && !couponCode) setCouponCode(r.data.coupon_code); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [token, open]);

  useEffect(() => { if (customer?.email) setEmail((prev) => prev || customer.email); }, [customer]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/coupon/validate",
        { code: couponCode, items: cartPayload },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (data.valid) { setCoupon(data); toast.success(data.message); }
      else { setCoupon(null); toast.error(data.message); }
    } catch { toast.error("Could not validate coupon."); }
  };

  const checkout = async () => {
    if (!items.length) return;
    if (rates.postal_valid === false) {
      toast.error("Please enter a valid postal code before checkout.");
      return;
    }
    if (!phone.trim()) {
      toast.error("Please enter a phone number before checkout.");
      return;
    }
    if (needsIdDoc && !idDocument.trim()) {
      toast.error("Please enter your ID / passport number (required for international customs).");
      return;
    }
    if (idDocument.trim() && !/^[A-Za-z0-9]{5,20}$/.test(idDocument.trim().replace(/\s/g, ""))) {
      toast.error("The ID / passport number looks invalid. Use 5–20 letters and numbers (no symbols).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: cartPayload,
        origin_url: window.location.origin,
        currency: currency,
        shipping_country: country,
        shipping_option: selectedRateId,
        postal_code: postal,
        discount_code: coupon?.valid ? coupon.code : undefined,
        referral_code: referralCode || undefined,
        email: email || customer?.email,
        phone: phone.trim(),
        id_document: needsIdDoc ? idDocument.trim() : undefined,
        locale: i18n.language || "en",
        wants_invoice: wantsInvoice,
        nif: wantsInvoice ? (nif || undefined) : undefined,
        billing_name: wantsInvoice ? (billingName || undefined) : undefined,
      };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await api.post("/payments/checkout", payload, { headers });
      track("begin_checkout", { provider: "stripe", value: totalConv, currency });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Payment could not be started. Please check your details or try another method. Your cart is saved.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#0a0a0a] border-l border-white/10 text-white flex flex-col w-full sm:max-w-md" data-testid={CART.drawer}>
        <SheetHeader>
          <SheetTitle className="text-white font-display text-2xl">Your Cart</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <div data-testid={CART.empty} className="flex-1 flex flex-col items-center justify-center gap-4 text-white/60">
            <ShoppingBag size={40} strokeWidth={1.2} />
            <p>Your cart is empty.</p>
            <Link to="/preshhgo" onClick={() => setOpen(false)} className="preshh-btn-primary">Shop Preshh GO</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-6 space-y-4">
              {items.map((it) => (
                <div key={it.id} data-testid={CART.item(it.id)} className="flex gap-4 bg-white/5 rounded-xl p-3 border border-white/10">
                  <img src={it.image} alt={it.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm">{it.name}</p>
                        <p className="text-[#00B5D8] text-sm mt-1">{format(it.price)}</p>
                      </div>
                      <button data-testid={CART.remove(it.id)} onClick={() => remove(it.id)} className="text-white/50 hover:text-white transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 bg-black/50 rounded-full">
                      <button data-testid={CART.qtyDec(it.id)} onClick={() => setQty(it.id, it.quantity - 1)} className="p-2"><Minus size={14} /></button>
                      <span className="min-w-6 text-center text-sm">{it.quantity}</span>
                      <button data-testid={CART.qtyInc(it.id)} onClick={() => setQty(it.id, it.quantity + 1)} className="p-2"><Plus size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <p className="text-xs uppercase tracking-widest text-white/50">Shipping calculator</p>
              <div className="grid grid-cols-2 gap-2">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid={CART.countryShip} className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] text-white border-white/10">
                    {COUNTRIES.map(([c, name]) => (<SelectItem key={c} value={c}>{name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="cart-postal-code"
                  value={postal}
                  onChange={(e) => setPostal(e.target.value)}
                  placeholder={country === "BR" ? "CEP (e.g. 01310-100)" : "Postal code"}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              {country === "BR" && postal && rates.postal_valid === false && (
                <p className="flex items-center gap-2 text-xs text-red-400"><AlertCircle size={14} /> CEP inválido. Verifique e tente novamente.</p>
              )}
              {country === "BR" && rates.postal_meta?.city && (
                <p className="flex items-center gap-2 text-xs text-white/60"><Check size={14} className="text-[#00B5D8]" /> {rates.postal_meta.city}, {rates.postal_meta.state}</p>
              )}

              {ratesLoading ? (
                <p className="text-xs text-white/50">Calculating shipping…</p>
              ) : rates.blocked ? (
                <p data-testid="cart-shipping-blocked" className="flex items-start gap-2 text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/20 rounded-xl p-3">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" /> {rates.block_reason || "Shipping to this country isn't available online."}
                </p>
              ) : rates.postal_valid === false ? (
                <p className="text-xs text-white/50 italic">Enter a valid postal code to see shipping options.</p>
              ) : (
                <div className="space-y-2">
                  {rates.options.map((opt) => (
                    <label key={opt.id} className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedRateId === opt.id ? "border-[#00B5D8] bg-[#00B5D8]/5" : "border-white/10 hover:border-white/20"}`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="ship" checked={selectedRateId === opt.id} onChange={() => setSelectedRateId(opt.id)}
                          data-testid={`cart-rate-${opt.id}`} className="accent-[#00B5D8]" />
                        <div>
                          <p className="text-sm font-medium">{opt.label}</p>
                          <p className="text-xs text-white/50">{opt.estimated_days} business days</p>
                        </div>
                      </div>
                      <p className="text-sm font-semibold">{format(opt.price)}</p>
                    </label>
                  ))}
                  {rates.notes && (
                    <div data-testid="cart-shipping-notes" className="space-y-1 pt-0.5">
                      {rates.notes.fuel_surcharge_covered && (
                        <p className="flex items-start gap-1.5 text-[11px] leading-snug text-white/45">
                          <Check size={12} className="mt-0.5 shrink-0 text-[#00B5D8]" /> {t("cart.fuel_note")}
                        </p>
                      )}
                      {rates.notes.import_duties && (
                        <div data-testid="cart-import-duties-note" className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-[12px] leading-snug text-amber-200">
                          <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{t("cart.duties_note")}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 space-y-2">
                <Input
                  data-testid="cart-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for order & receipt"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
                <Input
                  data-testid="cart-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number (required for shipping) *"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
                {needsIdDoc && (
                  <div className="space-y-1.5 rounded-xl border border-[#00B5D8]/20 bg-[#00B5D8]/5 p-3" data-testid="cart-id-document-field">
                    <Input
                      data-testid="cart-id-document"
                      value={idDocument}
                      onChange={(e) => setIdDocument(e.target.value)}
                      placeholder="ID / passport number *"
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                    <p className="text-[11px] leading-snug text-white/45">Required by DHL customs for shipments outside the EU. Any customs duties and import taxes are the buyer's responsibility.</p>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none pt-1">
                  <input type="checkbox" data-testid="cart-wants-invoice" checked={wantsInvoice}
                    onChange={(e) => setWantsInvoice(e.target.checked)} className="accent-[#00B5D8] w-4 h-4" />
                  I need an invoice with my tax number (NIF)
                </label>
                {wantsInvoice && (
                  <div className="space-y-2 rounded-xl border border-[#00B5D8]/20 bg-[#00B5D8]/5 p-3" data-testid="cart-invoice-fields">
                    <Input data-testid="cart-nif" value={nif} onChange={(e) => setNif(e.target.value)}
                      placeholder="NIF / Tax ID" className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
                    <Input data-testid="cart-billing-name" value={billingName} onChange={(e) => setBillingName(e.target.value)}
                      placeholder="Full name or company (for invoice)" className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
                  </div>
                )}
              </div>

              <div className="flex justify-between text-sm text-white/70 pt-2">
                <span>Items</span><span>{formatAmount(subtotalConv)}</span>
              </div>
              <div className="flex justify-between text-sm text-white/70">
                <span>Shipping</span><span>{formatAmount(shipConv)}</span>
              </div>
              {vatRate > 0 && (
                <div className="flex justify-between text-sm text-white/70" data-testid="cart-vat-row">
                  <span>VAT ({Math.round(vatRate * 100)}%)</span><span>{formatAmount(vatConv)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-sm text-[#00B5D8]" data-testid="cart-discount-row">
                  <span>Birthday discount</span><span>−{formatAmount(discountConv)}</span>
                </div>
              )}

              {/* Coupon */}
              <div className="pt-2">
                <div className="flex gap-2">
                  <Input
                    data-testid="cart-coupon-input"
                    placeholder="Discount code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Button type="button" onClick={applyCoupon} data-testid="cart-coupon-apply"
                    className="bg-white/10 hover:bg-white/15 text-white rounded-full px-4">Apply</Button>
                </div>
                {coupon?.valid && (
                  <p className="mt-2 flex items-center gap-2 text-xs text-[#00B5D8]"><Gift size={14} /> {coupon.code}: −{format(coupon.discount_amount)} on accessories</p>
                )}
                {referralCode && (
                  <p className="mt-2 flex items-center gap-2 text-xs text-[#00B5D8]" data-testid="cart-referral-active"><Gift size={14} /> Friend referral applied ({referralCode})</p>
                )}
              </div>

              <div className="flex justify-between text-lg font-bold border-t border-white/10 pt-3">
                <span>Total</span>
                <span data-testid={CART.subtotal}>{formatAmount(totalConv)}</span>
              </div>
              {salesSuspended ? (
                <div data-testid="cart-sales-paused" className="rounded-xl bg-[#00B5D8]/10 border border-[#00B5D8]/30 p-4 text-sm text-[#7fe3f5]">
                  <p className="font-semibold text-white">{t("sales_paused_title")}</p>
                  <p className="mt-1 text-white/70">{t("sales_paused_body")}</p>
                </div>
              ) : (
                <>
                  {needsIdDoc && (
                    <p data-testid="cart-customs-reminder" className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-200/80">
                      <AlertCircle size={12} className="mt-0.5 shrink-0" /> {t("cart.duties_note")}
                    </p>
                  )}
                  <Button data-testid={CART.checkout} onClick={checkout} disabled={loading || rates.postal_valid === false || rates.blocked || !phone.trim() || (needsIdDoc && !idDocument.trim())}
                    className="w-full bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full py-6 text-base">
                    {loading ? "Processing…" : "Checkout Securely"}
                  </Button>
                  {!rates.blocked && (
                  <PayPalPanel
                    country={country}
                    postal={postal}
                    shippingOption={selectedRateId}
                    discountCode={coupon?.valid ? coupon.code : undefined}
                    currency={currency}
                    phone={phone}
                    idDocument={idDocument}
                    needsIdDoc={needsIdDoc}
                  />
                  )}
                </>
              )}
              <button onClick={clear} className="w-full text-xs text-white/50 hover:text-white pt-1">Clear cart</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
